import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Paper, Button, LinearProgress, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import userServices from '../../../services/users';
import { FileSVG, UploadSVG } from '../../../components';
import errors from '../../../constants/prospects/errors';
import { asyncService } from '../../../helpers/AsyncService';
import { palette } from '../../../styles/theme';
import CustomAlert from '../../../components/Alert';
import { ROUTES } from '../../../constants/routes/routes';

const ALLOWED_EXTENSIONS = ['xls', 'xlsx'];
const MAX_FILE_SIZE_BYTES = 1024 * 1024;
const DIRNAME = 'consultants';
const FILE_PREFIX = 'Prospect-';
const MIME_TYPES = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    csv: 'text/csv'
};

export default function ConsultantsSection () {
    const { t } = useTranslation();
    const history = useHistory();
    const [files, setFiles] = useState([]);
    const [fileData, setFileData] = useState([]);
    const [loaderFile, setLoaderFile] = useState(false);
    const [showFileData, setShowFileData] = useState(false);
    const [progress, setProgress] = useState(0);
    const [openAlert, setOpenAlert] = useState(false);
    const [upload, setUpload] = useState(false);
    const [response, setResponse] = useState();
    const [errorResponse, setErrorResponse] = useState();

    const canUpload = !files.length || progress > 0;

    // Alert states
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop: async (acceptedFiles) => {
            let droppedFileName = '';
            const validFiles = acceptedFiles.filter((file) => {
                const fileExtension = file.name.split('.').pop().toLowerCase();
                const isValidExtension = ALLOWED_EXTENSIONS.includes(fileExtension);
                const isValidSize = file.size > 0 && file.size <= MAX_FILE_SIZE_BYTES;

                if (!isValidExtension) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'error',
                        className: 'clientsMC_Alert--error',
                        title: t('management.consultants.alert.error.extension'),
                    }));
                    setOpenAlert(true);
                }
                else if (!isValidSize) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'error',
                        className: 'clientsMC_Alert--error',
                        title: t('management.consultants.alert.error.size'),
                    }));
                    setOpenAlert(true);
                }

                if (isValidExtension && isValidSize) {
                    droppedFileName = `${FILE_PREFIX}${Date.now()}.${fileExtension}`;
                    setFileData([{ name: droppedFileName, size: (file.size / MAX_FILE_SIZE_BYTES).toFixed(3), upload: false }]);
                }

                return isValidExtension && isValidSize;
            });

            if (validFiles.length > 0) {
                setLoaderFile(true);
                setShowFileData(true);
                setResponse();
                setErrorResponse();

                const fileToUpload = validFiles[0];
                const fileExtension = fileToUpload.name.split('.').pop().toLowerCase();
                const contentType = fileToUpload.type || MIME_TYPES[fileExtension] || 'application/octet-stream';

                try {
                    const fileUrl = await userServices.uploadS3({
                        fileUri: fileToUpload,
                        filename: droppedFileName,
                        dirname: DIRNAME,
                        contentType: contentType
                    });

                    setFiles([fileUrl]);
                    setFileData((prevFileData) =>
                        prevFileData.map((fd) =>
                            fd.name === droppedFileName
                                ? { ...fd, upload: true }
                                : fd
                        )
                    );
                }
                catch (error) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'error',
                        className: 'clientsMC_Alert--error',
                        title: t('management.consultants.alert.error.upload'),
                    }));
                    setOpenAlert(true);
                    setFileData([]);
                    setFiles([]);
                    setShowFileData(false);
                }
                finally {
                    setLoaderFile(false);
                    setProgress(100);
                }
            }
        },
        noClick: true,
    });

    const handleCancel = () => {
        setFileData([]);
        setFiles([]);
        setShowFileData(false);
        setResponse();
        setErrorResponse();
        history.push(ROUTES.MANAGEMENT.CONSULTANTS);
    };

    const handleConfirm = async () => {
        setUpload(true);

        try {
            const urlValid = await userServices.validateFiles({ url: files[0].url });
            const response = await asyncService((qs) => userServices.processFile({ url: urlValid.url }, qs));
            const results = response?.task?.result;

            setResponse(
                {
                    newConsultants: results.result.created,
                    updatedConsultants: results.result.updated,
                    totalConsultants: results.result.total,

                }
            );
        }
        catch (error) {
            setErrorResponse({ key: error.response?.data?.extra?.key, row: error.response?.data?.extra?.row });
        }
        finally {
            setFileData([]);
            setFiles([]);
            setUpload(false);
        }
    };

    useEffect(() => {
        let timer;
        if (loaderFile) {
            timer = setInterval(() => {
                setProgress((prevProgress) => (prevProgress >= 100 ? 100 : prevProgress + 10));
            }, 600);
        }
        else if (progress >= 100) {
            setProgress(0);
        }

        return () => {
            clearInterval(timer);
        };
    }, [loaderFile, progress]);

    return (
        <Box>
            {(response || errorResponse) && (
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 2
                }}>
                    {response ? <CheckCircleIcon size={25} sx={{ color: palette.success[500], width: '2em', height: '2em' }}/> : <CancelIcon size={25} sx={{ color: palette.error.main, width: '2em', height: '2em' }}/>}
                    <Typography
                        variant="h1"
                        color="textPrimary"
                        sx={{
                            mt: 2,
                            fontSize: { xs: '1.385rem', sm: '1.615rem', md: '1.846rem' },
                            textAlign: 'center',
                            width: '100%',
                            maxWidth: '46.154rem'
                        }}
                    >
                        {response ? t('management.consultants.success') : t('management.consultants.fail')}
                    </Typography>
                    <Typography
                        color={palette.disabled.main}
                        sx={{
                            mt: 2,
                            mb: 5,
                            fontSize: { xs: '0.846rem', sm: '0.923rem' },
                            textAlign: 'center',
                            width: '100%',
                            maxWidth: '38.462rem'
                        }}
                    >
                        {response
                            ? (
                                <>
                                    {t('management.consultants.constants.created')} {response.newConsultants} {t('management.consultants.constants.consultants')}<br />
                                    {t('management.consultants.constants.updated')} {response.updatedConsultants} {t('management.consultants.constants.consultants')}<br />
                                    {t('management.consultants.constants.total')} {response.totalConsultants}
                                </>
                            )
                            : (errorResponse.row ? t('management.consultants.constants.file') + `: ${errorResponse.row}. ` : '') + t(`management.consultants.validateErrors.${errors[errorResponse.key].text}`)
                        }
                    </Typography>
                </Box>
            )}
            <Paper
                {...getRootProps()}
                elevation={0}
                sx={{
                    minHeight: showFileData || response ? '7.692rem' : '15.385rem',
                    height: 'auto',
                    padding: 2,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.300',
                    backgroundColor: isDragActive ? 'grey.100' : 'background.paper',
                    cursor: 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <CustomAlert
                    openAlert={openAlert}
                    setOpenAlert={setOpenAlert}
                    alert={alert}
                    setAlert={setAlert}
                />
                <input {...getInputProps()} />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: showFileData || response ? 'row' : 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: showFileData || response ? 'wrap' : 'nowrap',
                        width: '100%',
                        py: showFileData || response ? 1 : 0
                    }}
                >
                    <Box sx={{ mb: 0, mt: showFileData || response ? 0 : 1, display: 'flex', alignItems: 'center' }}>
                        <UploadSVG />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: showFileData || response ? 'flex-start' : 'center',
                            justifyContent: 'center',
                            textAlign: showFileData || response ? 'left' : 'center',
                            width: showFileData || response ? 'auto' : '100%',
                            flex: showFileData || response ? '1 1 auto' : 'none',
                            ml: showFileData || response ? 3 : 0,
                            my: showFileData || response ? 1 : 0
                        }}
                    >
                        <Typography
                            variant="h2"
                            color="primary.main"
                            sx={{
                                mt: showFileData || response ? 0 : 2,
                                fontSize: { xs: '1.231rem', sm: '1.538rem', md: '1.846rem' },
                                fontWeight: 500,
                                lineHeight: 1.3
                            }}
                        >
                            {isDragActive ? t('management.consultants.dragZone.active') : t('management.consultants.dragZone.inactive')}
                        </Typography>
                        <Typography
                            variant="h1"
                            color="disabled.main"
                            sx={{
                                mt: showFileData || response ? 0.5 : 1,
                                fontSize: { xs: '0.846rem', sm: '0.923rem', md: '1.077rem' },
                                lineHeight: 1.3
                            }}
                            fontWeight={400}
                        >
                            {t('management.consultants.dragZone.subtitle')}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={open}
                        disabled={progress > 0}
                        sx={{
                            py: '11px',
                            px: 3,
                            my: showFileData || response ? 1 : 2,
                            mx: showFileData || response ? 2 : 0,
                            minWidth: showFileData || response ? '15.385rem' : 'auto',
                            textTransform: 'capitalize',
                            borderRadius: '50px',
                            boxShadow: 'none',
                            flexShrink: 0,
                            backgroundColor: palette.primary.main,
                            '&:hover': {
                                backgroundColor: palette.primary.main,
                                boxShadow: 'none'
                            },
                        }}
                    >
                        {t('management.consultants.dragZone.buttonDrag')}
                    </Button>
                </Box>
            </Paper>
            {showFileData && (
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 4 }}>
                    {fileData.map((file, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <FileSVG/>
                            <Box sx={{ width: '100%', ml: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color={progress === 0 ? palette.disabled[950] : palette.disabled[500]}>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="body2" color={palette.disabled[950]}>
                                        {`${file.size} MB`}
                                    </Typography>
                                </Box>
                                {loaderFile && file.upload === false && (
                                    <LinearProgress variant="determinate" value={progress} sx={{
                                        mt: 1,
                                        borderRadius: 8,
                                        backgroundColor: palette.disabled[100],
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: palette.tertiary[600]
                                        }
                                    }} />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    justifyContent: 'right',
                    height: '100%',
                    width: '100%',
                    mt: 5
                }}
            >
                <Button
                    variant='text'
                    onClick={handleCancel}
                    disabled={loaderFile || upload}
                    sx={{
                        color: palette.secondary[600],
                        mx: 2,
                        minWidth: '100px',
                        textTransform: 'capitalize',
                        boxShadow: 'none',
                        padding: '11px 32px',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            boxShadow: 'none'
                        },
                        '&:disabled': {
                            color: palette.disabled.main,
                        }

                    }}>
                    {t('management.consultants.buttonCancel')}
                </Button>
                <Button
                    variant='contained'
                    onClick={handleConfirm}
                    disabled={canUpload || upload}
                    sx={{
                        backgroundColor: palette.secondary[600],
                        color: 'white',
                        mx: 2,
                        minWidth: '100px',
                        textTransform: 'capitalize',
                        borderRadius: '50px',
                        boxShadow: 'none',
                        padding: '11px 32px',
                        '&:hover': {
                            backgroundColor: palette.secondary[600],
                            boxShadow: 'none'
                        },
                        '&:disabled': {
                            backgroundColor: palette.disabled[500],
                            color: 'white',
                        }

                    }}
                >
                    {upload ? (<CircularProgress size={24} style={{ color: 'white' }} />) : t('management.consultants.buttonConfirm')}
                </Button>
            </Box>
        </Box>
    );
}
